import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button } from 'antd';

import PageLayoutRender from '../components/layouts/PageLayoutRender';
import { offFormBlocks, sheetPageRules } from '../components/layouts/pageLayoutForm';
import { CollectionScope } from '../contexts/CollectionScopeContext';
import QueryBlock from '../components/layouts/QueryBlock';
import useLinkedItems from '../hooks/useLinkedItems';
import useFormPageImages from '../hooks/useFormPageImages';
import useBlockFit from '../hooks/useBlockFit';
import PageFitNotice from '../components/layouts/PageFitNotice';
import { useCardDefinitions } from '../contexts/CardDefinitionsContext';
import { createLookupResolver } from '../utils/lookups';
import { getPageLayouts } from '../services/cardLayouts';
import { normalizeId } from '../utils/ids';
import { api } from '../api/client';
import { API_ITEMS_URL } from '../utils';
import { DataState, Skeleton, deriveDataState } from '../components/loading';
import { anchorOf, print as platformPrint } from '../platform';

// A layout that is gone, or is not this person's, answers 403/404 (or is simply
// absent from the card's list). Anything else - no network, a 5xx - is a
// failure to read it, and is worth another try.
const isMissing = (error) => error?.message === 'not-found' || [403, 404].includes(error?.response?.status);

/**
 * A stored page layout, rendered as paper.
 *
 * Deliberately outside the app shell: no rail, no context bar, nothing but the
 * sheet and one button that is itself hidden when printing. What is on screen
 * here IS what comes out of the printer, because the sheet is drawn at its
 * real physical size and `@page` is written from the same stored body.
 *
 * `@page` cannot read a CSS variable, so the size is injected as a <style> tag
 * per layout — the one piece of the sheet that cannot be expressed in the
 * component's own styles.
 *
 * A layout with a form (KPR-1860) waits for its pages. Print stays off until
 * every page is in hand and decoded, and a sheet whose page is not draws its
 * waiting state and no values (PageLayoutRender), so a browser's own Print menu
 * cannot put a record on blank paper as if it were the form. A refused page is
 * the same sentence as a layout the reader cannot see: the same reader, asked
 * the same question a moment later.
 */
export default function PrintPage() {
	const { collectionId, cardId, layoutId } = useParams();
	const [searchParams] = useSearchParams();
	const itemId = searchParams.get('item');

	const { cardsById, getElementsForCard, loading: cardsLoading } = useCardDefinitions();
	const [layout, setLayout] = useState(null);
	const [item, setItem] = useState(null);
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(true);
	// Retry bumps this so the layout is read again.
	const [reloadKey, setReloadKey] = useState(0);

	useEffect(() => {
		let active = true;
		setLoading(true);
		setError(null);
		Promise.all([
			getPageLayouts(cardId, { collectionId }),
			itemId ? api.get(`${API_ITEMS_URL}/${itemId}`).then(({ data }) => data) : Promise.resolve(null),
		])
			.then(([layouts, record]) => {
				if (!active) return;
				const found = (layouts || []).find(row => normalizeId(row._id) === normalizeId(layoutId));
				if (!found) throw new Error('not-found');
				setLayout(found);
				setItem(record);
				setError(null);
			})
			.catch(e => { if (active) setError(e); })
			.finally(() => { if (active) setLoading(false); });
		return () => { active = false; };
	}, [cardId, collectionId, layoutId, itemId, reloadKey]);

	const card = cardsById?.[normalizeId(cardId)] || null;
	const elementDefs = useMemo(
		() => (getElementsForCard ? getElementsForCard(cardId) : []),
		[getElementsForCard, cardId]
	);

	// A printed record resolves its lookups to titles the way every other read
	// surface does — a raw id on paper is worse than a blank.
	const linked = useLinkedItems({
		items: item ? [item] : [],
		getElementsForCard,
		enabled: !cardsLoading && Boolean(item),
		resetKey: `print-${layoutId}`,
		// A group's entries print one by one: a lookup column needs its title (KPR-1863).
		entries: true,
	});
	const resolveLookupValue = useMemo(() => createLookupResolver({
		itemsById: linked,
		cardsById,
		getElementsForCard,
	}), [linked, cardsById, getElementsForCard]);

	const page = layout?.body?.page;
	const form = layout?.body?.form || null;
	const formImages = useFormPageImages({ cardId, layoutId, form, collectionId });
	// @page has no access to custom properties, so the sheet's size reaches
	// the printer through a stylesheet written from the stored body.
	const pageRule = page?.width ? sheetPageRules(layout.body) : '';
	const offForm = form ? offFormBlocks(layout.body) : [];
	const retryForm = () => {
		// A page refused as gone means the form was replaced under this tab: the
		// layout has to be read again to know the new one.
		setReloadKey(n => n + 1);
		formImages.retry();
	};

	// What a form's boxes cut of THIS record's values, told before Print is pressed
	// and never on the paper (KPR-1861). Measured once every page is in and the
	// sheets draw their values; a plain layout measures nothing.
	const [sheetRoot, setSheetRoot] = useState(null);
	// A layout whose form was taken off keeps its group blocks, whose entries can still run out of lines.
	const measured = form ? formImages.state === 'ready' : (layout?.body?.blocks || []).some(block => block.type === 'group');
	const cuts = useBlockFit(sheetRoot, { enabled: measured && !cardsLoading });

	const print = useCallback((event) => { platformPrint(undefined, { anchor: anchorOf(event) }); }, []);

	useEffect(() => {
		if (layout?.name) document.title = layout.name;
	}, [layout]);

	if ((error && isMissing(error)) || formImages.state === 'missing') {
		return (
			<Alert
				type="error"
				showIcon
				message="That page layout is not available."
				description="It may have been deleted, or belong to someone else. Go back and pick another."
			/>
		);
	}
	if (!layout) {
		return (
			<DataState
				state={deriveDataState({ loading: loading || cardsLoading, error })}
				skeleton={<Skeleton variant="block" height={320} />}
				error={error ? 'Check your connection and try again.' : null}
				onRetry={() => setReloadKey(n => n + 1)}
				label="this page layout"
			/>
		);
	}
	if (cardsLoading) return <Skeleton variant="block" height={320} />;

	return (
		<div ref={setSheetRoot} className={`print-page${form ? ' print-page--form' : ''}`}>
			{pageRule ? <style>{pageRule}</style> : null}
			<div className="print-page__bar print-hide">
				<span className="print-page__name">{layout.name}</span>
				<Button type="primary" disabled={Boolean(form) && formImages.state !== 'ready'} onClick={print}>Print</Button>
			</div>
			{form ? (
				<p className="print-page__note print-hide">
					Print at 100% with margins set to none, so each value lands on its line.
				</p>
			) : null}
			{/* Before the dialog, on the screen: what the boxes cut of this record. */}
			<PageFitNotice cuts={cuts} pages={form?.pages?.length || 1} className="print-hide" />
			{offForm.length ? (
				<p className="print-page__note print-hide">
					{offForm.length === 1 ? '1 block is off the form and may not print.' : `${offForm.length} blocks are off the form and may not print.`}
				</p>
			) : null}
			{/* Paper reads a reference as its title: no qualified badges
			    on a printed sheet (contexts/CollectionScopeContext). */}
			<CollectionScope qualify={false}>
				<PageLayoutRender
					layout={layout}
					card={card}
					elementDefs={elementDefs}
					item={item}
					resolveLookupValue={resolveLookupValue}
					unitSystem="default"
					formImages={formImages.pages}
					onRetryForm={retryForm}
					renderQueryBlock={(block, record) => (
						<QueryBlock
							block={block}
							item={record}
							collectionId={collectionId}
							cardsById={cardsById}
							getElementsForCard={getElementsForCard}
							resolveLookupValue={resolveLookupValue}
						/>
					)}
				/>
			</CollectionScope>
		</div>
	);
}
